import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { T, IMAGES } from "@/content/site";
import { api } from "@/lib/api";

const F = (v) => (v ? "filled" : "");

export default function Gifting() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", whatsapp: "", units: "", occasion: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/gifting", form);
      toast.success(t({ es: "Solicitud recibida. Te enviaremos una propuesta.", en: "Request received. We'll send a proposal." }));
      setForm({ name: "", company: "", email: "", country: "", whatsapp: "", units: "", occasion: "", message: "" });
    } catch {
      toast.error(t({ es: "No pudimos enviar la solicitud.", en: "We couldn't send the request." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="gifting" data-testid="gifting-section" className="relative bg-[#0F0F0F] py-[140px] md:py-[180px] border-t border-white/5 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-14">
          <div className="col-span-12 md:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.06 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
              className="img-hover aspect-[4/5] md:aspect-[3/4] overflow-hidden"
            >
              <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_mezcal-luxury/artifacts/2xjyn5ta_WhatsApp%20Image%202026-08-03%20at%2010.43.44.jpeg" alt="Corporate gifting" className="w-full h-full object-cover" />
            </motion.div>
            <div className="hidden md:flex absolute top-6 right-6 items-center gap-3 bg-black/50 backdrop-blur-md px-4 py-2 border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--sacrvm-gold)]" />
              <span className="text-[11px] tracking-[0.24em] uppercase text-white/80">{t({ es: "Empaque a medida", en: "Bespoke packaging" })}</span>
            </div>
          </div>

          <div className="col-span-12 md:col-span-6 md:pl-6 flex flex-col justify-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
              {t(T.gifting.overline)}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-white text-[36px] md:text-[56px] leading-[1.05] tracking-[-0.01em]"
            >
              {t(T.gifting.title)}
            </motion.h2>
            <p className="mt-8 text-[color:var(--sacrvm-text-muted)] text-[16px] leading-[1.75] max-w-[520px]">
              {t(T.gifting.body)}
            </p>

            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {T.gifting.perks.map((p, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="flex items-baseline gap-3 py-2 border-b border-white/10 text-white/85 text-[14px]"
                >
                  <span className="text-[color:var(--sacrvm-gold)] text-[10px]">◆</span>
                  {t(p)}
                </motion.li>
              ))}
            </ul>

            <form onSubmit={submit} data-testid="gifting-form" className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <div className={"field " + F(form.name)}>
                <label>{t({ es: "Nombre", en: "Name" })}</label>
                <input data-testid="gift-name" value={form.name} onChange={set("name")} required />
              </div>
              <div className={"field " + F(form.company)}>
                <label>{t({ es: "Empresa", en: "Company" })}</label>
                <input data-testid="gift-company" value={form.company} onChange={set("company")} />
              </div>
              <div className={"field " + F(form.email)}>
                <label>{t({ es: "Correo", en: "Email" })}</label>
                <input data-testid="gift-email" type="email" value={form.email} onChange={set("email")} required />
              </div>
              <div className={"field " + F(form.whatsapp)}>
                <label>WhatsApp</label>
                <input data-testid="gift-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} />
              </div>
              <div className={"field " + F(form.units)}>
                <label>{t({ es: "Unidades estimadas", en: "Estimated units" })}</label>
                <input data-testid="gift-units" value={form.units} onChange={set("units")} />
              </div>
              <div className={"field " + F(form.occasion)}>
                <label>{t({ es: "Ocasión (boda, cliente, evento…)", en: "Occasion (wedding, client, event…)" })}</label>
                <input data-testid="gift-occasion" value={form.occasion} onChange={set("occasion")} />
              </div>
              <div className={"field md:col-span-2 " + F(form.message)}>
                <label>{t({ es: "Detalles", en: "Details" })}</label>
                <textarea data-testid="gift-message" rows={2} value={form.message} onChange={set("message")} />
              </div>
              <div className="md:col-span-2 mt-8">
                <button data-testid="gift-submit" disabled={loading} className="btn-sharp gold">
                  {loading ? t({ es: "Enviando…", en: "Sending…" }) : t(T.gifting.cta)}
                  <ArrowRight size={14} className="arrow" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
